/**
 * Credits Service 实现
 */

import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { createPaymentSession } from '@eureka/shared/plg-plan/src/apis/stripe-api';
import type { CreditsApi, SwitchTenantParams, SwitchTenantResponse } from '../apis/credits-api';
import {
    fetchCurrentCreditsPriceRegion,
    getCreditsStripePriceId,
} from '@eureka/shared/plg-plan/src/constants';
import type { CreditsTier } from '@eureka/shared/plg-plan/src/types';
import { CreditsApi as CreditsApiToken } from '../apis/credits-api';
import type { CreditsService } from './credits-service';
import type {
    CreditsSummary,
    UsageRecord,
    CreditChangeRecord,
    EntitlementContext,
    WalletStatus,
    UpgradeConfirmInfo,
    DowngradeConfirmInfo,
    GetUsageRecordsParams,
    GetCreditChangesParams,
    TierLevel,
    RechargeRecordsResponse,
    ConsumeRecordsParams,
    ConsumeRecordsResponse,
    PersonalCreditsDetailResponse,
    CompanyCreditsDetailResponse,
    RechargeEligibilityInfo,
    ActivityRechargeParams,
    ActivityRechargeResponse,
} from '../types';
import {
    getMockSummary,
    getMockUsageRecords,
    MOCK_CREDIT_CHANGES,
    MOCK_UPGRADE_CONFIRM_INFO,
    MOCK_DOWNGRADE_CONFIRM_INFO,
    MOCK_RECHARGE_RECORDS,
    MOCK_CONSUME_RECORDS,
    MOCK_PERSONAL_CREDITS_DETAIL,
    MOCK_COMPANY_CREDITS_DETAIL,
} from '../mock/credits-mock-data';

const USE_MOCK = false;

@injectable()
export class CreditsServiceImpl implements CreditsService {
    @inject(CreditsApiToken) private creditsApi!: CreditsApi;

    async getCreditsSummary(): Promise<CreditsSummary> {
        if (USE_MOCK) {
            await this.delay(500);
            return getMockSummary();
        }
        const response = await this.creditsApi.getCreditsSummary();
        return (response as any).summary ?? response;
    }

    async getUsageRecords(params?: GetUsageRecordsParams): Promise<{
        records: UsageRecord[];
        total: number;
        page: number;
        pageSize: number;
    }> {
        if (USE_MOCK) {
            await this.delay(300);
            const page = params?.page || 1;
            const pageSize = params?.pageSize || 20;
            const start = (page - 1) * pageSize;
            const end = start + pageSize;
            const mockRecords = getMockUsageRecords();
            const paginatedRecords = mockRecords.slice(start, end);
            return { records: paginatedRecords, total: mockRecords.length, page, pageSize };
        }
        const response = await this.creditsApi.getUsageRecords(params);
        return {
            records: response.records ?? [],
            total: response.total ?? 0,
            page: response.page ?? params?.page ?? 1,
            pageSize: response.pageSize ?? params?.pageSize ?? 20,
        };
    }

    async getCreditChanges(params?: GetCreditChangesParams): Promise<{
        records: CreditChangeRecord[];
        total: number;
        page: number;
        pageSize: number;
    }> {
        if (USE_MOCK) {
            await this.delay(300);
            const page = params?.page || 1;
            const pageSize = params?.pageSize || 20;
            const start = (page - 1) * pageSize;
            const end = start + pageSize;
            const paginatedRecords = MOCK_CREDIT_CHANGES.slice(start, end);
            return { records: paginatedRecords, total: MOCK_CREDIT_CHANGES.length, page, pageSize };
        }
        const response = await this.creditsApi.getCreditChanges(params);
        return {
            records: response.records ?? [],
            total: response.total ?? 0,
            page: response.page ?? params?.page ?? 1,
            pageSize: response.pageSize ?? params?.pageSize ?? 20,
        };
    }

    async getRechargeRecords(): Promise<RechargeRecordsResponse> {
        if (USE_MOCK) {
            await this.delay(300);
            return MOCK_RECHARGE_RECORDS;
        }
        const response = await this.creditsApi.getPersonalRechargeRecords();
        return {
            totalCredits: response.total_credits,
            usedCredits: response.used_credits,
            items: (response.items || []).map(item => ({
                tenantUserId: item.tenant_user_id,
                credits: item.credits,
                source: item.source,
                sourceId: item.source_id,
                beginAt: item.begin_at,
                endAt: item.end_at,
                remark: item.remark,
                rechargeAt: item.recharge_at,
            })),
        };
    }

    async getConsumeRecords(params?: ConsumeRecordsParams): Promise<ConsumeRecordsResponse> {
        if (USE_MOCK) {
            await this.delay(300);
            const pageSize = params?.pageSize || 20;
            let items = MOCK_CONSUME_RECORDS.items;
            if (params?.cursorId) {
                const cursorIndex = items.findIndex(item => item.id === params.cursorId);
                if (cursorIndex !== -1) {
                    items = items.slice(cursorIndex + 1);
                }
            }
            const paginatedItems = items.slice(0, pageSize);
            const hasMore = items.length > pageSize;
            return {
                items: paginatedItems,
                totalSize: MOCK_CONSUME_RECORDS.items.length,
                pageSize,
                nextCursorId: hasMore ? paginatedItems[paginatedItems.length - 1]?.id : undefined,
                nextCursorCreatedAt: hasMore ? paginatedItems[paginatedItems.length - 1]?.createdAt : undefined,
            };
        }
        return this.creditsApi.getConsumeRecords(params);
    }

    async getEntitlementContext(): Promise<EntitlementContext> {
        if (USE_MOCK) {
            await this.delay(200);
            throw new Error('Mock EntitlementContext not implemented');
        }
        const response = await this.creditsApi.getEntitlementContext();
        return (response as any).context ?? response;
    }

    async getWalletStatus(): Promise<WalletStatus> {
        if (USE_MOCK) {
            await this.delay(200);
            throw new Error('Mock WalletStatus not implemented');
        }
        const response = await this.creditsApi.getWalletStatus();
        return (response as any).wallet ?? response;
    }

    async getUpgradeConfirmInfo(targetTier: TierLevel): Promise<UpgradeConfirmInfo> {
        if (USE_MOCK) {
            await this.delay(300);
            return { ...MOCK_UPGRADE_CONFIRM_INFO, targetTier };
        }
        const response = await this.creditsApi.getUpgradeConfirmInfo({ targetTier });
        return (response as any).confirmInfo ?? response;
    }

    async executeUpgrade(targetTier: TierLevel): Promise<{ success: boolean; checkoutUrl?: string; error?: string }> {
        const priceRegion = await fetchCurrentCreditsPriceRegion();
        const priceId = getCreditsStripePriceId(targetTier as unknown as CreditsTier, 'monthly', priceRegion);
        if (!priceId) {
            return { success: false, error: `No Stripe price ID configured for tier: ${targetTier}` };
        }

        try {
            const response = await createPaymentSession({
                price_ids: [priceId],
                mode: 'subscription',
                ui_mode: 'hosted',
                success_url: `${window.location.origin}/credits?payment_state=success&tier=${targetTier}`,
                cancel_url: `${window.location.origin}/credits?payment_state=cancelled`,
            });
            return { success: true, checkoutUrl: response.url };
        } catch (error) {
            console.error('Failed to create payment session:', error);
            return { success: false, error: error instanceof Error ? error.message : 'Failed to create payment session' };
        }
    }

    async getDowngradeConfirmInfo(targetTier: TierLevel): Promise<DowngradeConfirmInfo> {
        if (USE_MOCK) {
            await this.delay(300);
            return { ...MOCK_DOWNGRADE_CONFIRM_INFO, targetTier };
        }
        const response = await this.creditsApi.getDowngradeConfirmInfo({ targetTier });
        return (response as any).confirmInfo ?? response;
    }

    async executeDowngrade(targetTier: TierLevel): Promise<{ success: boolean; scheduledEffectiveAt?: string; error?: string }> {
        if (USE_MOCK) {
            await this.delay(500);
            return { success: true, scheduledEffectiveAt: '2026-06-01T00:00:00Z' };
        }
        const response = await this.creditsApi.executeDowngrade({ targetTier });
        return {
            success: response.success,
            scheduledEffectiveAt: response.scheduledEffectiveAt,
            error: response.error,
        };
    }

    async cancelDowngrade(): Promise<{ success: boolean; error?: string }> {
        if (USE_MOCK) {
            await this.delay(300);
            return { success: true };
        }
        const response = await this.creditsApi.cancelDowngrade();
        return { success: response.success, error: response.error };
    }

    async claimFreeCredits(): Promise<{ success: boolean; creditsGranted?: number; expiresAt?: string; error?: string }> {
        if (USE_MOCK) {
            await this.delay(500);
            return { success: true, creditsGranted: 5000, expiresAt: '2026-06-19T00:00:00Z' };
        }
        const response = await this.creditsApi.claimFreeCredits();
        return {
            success: response.success,
            creditsGranted: response.creditsGranted,
            expiresAt: response.expiresAt,
            error: response.error,
        };
    }

    async checkFreeCreditsEligibility(): Promise<{ eligible: boolean; nextClaimDate?: string }> {
        if (USE_MOCK) {
            await this.delay(200);
            return { eligible: false, nextClaimDate: '2026-06-01T00:00:00Z' };
        }
        return this.creditsApi.checkFreeCreditsEligibility();
    }

    async getPersonalCreditsDetail(_tenantUserId: string): Promise<PersonalCreditsDetailResponse> {
        if (USE_MOCK) {
            await this.delay(300);
            return MOCK_PERSONAL_CREDITS_DETAIL;
        }
        // tenantUserId 通过 header x-user-id 自动注入，无需在请求参数中传递
        return this.creditsApi.getPersonalCreditsDetail(_tenantUserId);
    }

    async getCompanyCreditsDetail(_tenantUserId: string, _companyId: string): Promise<CompanyCreditsDetailResponse> {
        if (USE_MOCK) {
            await this.delay(300);
            return MOCK_COMPANY_CREDITS_DETAIL;
        }
        // tenantUserId / companyId 通过 header x-user-id / x-tenant-id 自动注入
        return this.creditsApi.getCompanyCreditsDetail(_tenantUserId, _companyId);
    }

    async getRechargeEligibility(): Promise<RechargeEligibilityInfo> {
        if (USE_MOCK) {
            await this.delay(200);
            return {
                has_recharge_records: false,
                is_personal_account: true,
                is_latest_recharge_over_30_days: true,
            };
        }
        const response = await this.creditsApi.getRechargeEligibility();
        return response.data;
    }

    async rechargeActivityCredits(params: ActivityRechargeParams): Promise<ActivityRechargeResponse> {
        if (USE_MOCK) {
            await this.delay(500);
            return {
                status: true,
                error_code: 0,
                message: 'Success',
                data: {
                    recharge_record_id: 'mock_recharge_record_id',
                },
            };
        }
        return this.creditsApi.rechargeActivityCredits(params);
    }

    async switchTenant(data: SwitchTenantParams | string): Promise<SwitchTenantResponse> {
        return this.creditsApi.switchTenant(data);
    }

    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
